import {
  ghostBtnClass,
  inputClass,
  mfgPrimaryBtnClass,
  mfgSolidDangerBtnClass,
} from "@/components/manufacturing/manufacturingSurfaceClasses";
import type { MfgRecipeSummary } from "@/lib/manufacturing/types";
import { ManufacturingModelsDictateMicButton } from "./ManufacturingModelsDictateMicButton";

export type RestartMode = "wipe" | "template" | "describe";

export function ManufacturingModelsRestartChooser({
  mode,
  onMode,
  templateId,
  onTemplateId,
  templateOptions,
  brief,
  onBrief,
  error,
  onCancel,
  onContinue,
}: {
  mode: RestartMode;
  onMode: (m: RestartMode) => void;
  templateId: string;
  onTemplateId: (id: string) => void;
  templateOptions: readonly MfgRecipeSummary[];
  brief: string;
  onBrief: (v: string) => void;
  error: string | null;
  onCancel: () => void;
  onContinue: () => void;
}) {
  return (
    <div className="mt-4 space-y-3">
      {(
        [
          {
            id: "wipe" as const,
            title: "Wipe and start fresh",
            body: "Clear BOM lines, tags, notes, and attachments. Code and name stay.",
          },
          {
            id: "template" as const,
            title: "Start from another model",
            body: "Copy that model’s BOM, tags, and notes — or add a short brief so AI adapts the BOM.",
          },
          {
            id: "describe" as const,
            title: "Describe changes for AI",
            body: "AI rebuilds from the current BOM using your instructions.",
          },
        ] as const
      ).map((opt) => (
        <label
          key={opt.id}
          className={`block cursor-pointer rounded-lg border px-3 py-2 text-sm ${
            mode === opt.id
              ? "border-indigo-400 bg-indigo-50 dark:border-indigo-600 dark:bg-indigo-950/40"
              : "border-gray-200 dark:border-zinc-700"
          }`}
        >
          <div className="flex items-start gap-2">
            <input
              type="radio"
              className="mt-1"
              checked={mode === opt.id}
              onChange={() => onMode(opt.id)}
            />
            <div>
              <div className="font-medium text-gray-900 dark:text-zinc-100">{opt.title}</div>
              <div className="text-xs text-gray-600 dark:text-zinc-400">{opt.body}</div>
            </div>
          </div>
        </label>
      ))}

      {mode === "template" ? (
        <label className="block text-xs text-gray-600 dark:text-zinc-300">
          Template model
          <select
            className={`${inputClass} mt-1`}
            value={templateId}
            onChange={(e) => onTemplateId(e.target.value)}
          >
            <option value="">Select…</option>
            {templateOptions.map((r) => (
              <option key={r.id} value={r.id}>
                {r.code} · {r.name}
              </option>
            ))}
          </select>
        </label>
      ) : null}

      {mode === "template" || mode === "describe" ? (
        <div className="space-y-2">
          <label className="block text-xs text-gray-600 dark:text-zinc-300">
            {mode === "template" ? "Optional AI adaptation brief" : "What should change"}
            <textarea
              className={`${inputClass} mt-1 min-h-[88px]`}
              value={brief}
              onChange={(e) => onBrief(e.target.value)}
              placeholder={
                mode === "template"
                  ? "e.g. Same floor package but Utility roof finish instead of Lofted…"
                  : "e.g. Strip finish package lines and rebuild for a 12x16 Utility…"
              }
            />
          </label>
          <div className="flex justify-end">
            <ManufacturingModelsDictateMicButton
              onComplete={(t) => onBrief(brief.trim() ? `${brief.trim()} ${t}` : t)}
            />
          </div>
        </div>
      ) : null}

      {error ? <p className="text-xs text-red-600 dark:text-red-400">{error}</p> : null}

      <div className="flex justify-end gap-2 pt-1">
        <button type="button" className={ghostBtnClass} onClick={onCancel}>
          Cancel
        </button>
        <button
          type="button"
          className={mode === "wipe" ? mfgSolidDangerBtnClass : mfgPrimaryBtnClass}
          onClick={onContinue}
        >
          {mode === "wipe" ? "Wipe model" : "Continue"}
        </button>
      </div>
    </div>
  );
}
